import request from "../helpers/request";
import { getAllServices } from "./cmsService";
import { getBlogCategories } from "./blogService";
import { getTeamById, getTeamProfessions } from "./teamService";
import { normalizeAgreement, normalizeCompanyDocument } from "./profileService";

export const USER_ROLE_KEY = "userRole";
export const USER_ROLE = {
  USER: "user",
  SPECIALIST: "specialist",
};

export const SPECIALIST_CABINET_PATH = "/mutaxassis";
export const CUSTOMER_CABINET_PATH = "/profile";

/** Müvəqqəti: kabineti loginsiz açır. Real giriş gələndə false edin. */
export const SPECIALIST_CABINET_PREVIEW = false;
export const SPECIALIST_PREVIEW_ID = 2;

const API_ORIGIN = String(
  process.env.REACT_APP_API_URL || "https://dev-api.lifechart.az"
).replace(/\/$/, "");

export const getStoredUserRole = () => {
  const role = localStorage.getItem(USER_ROLE_KEY);
  return role === USER_ROLE.SPECIALIST ? USER_ROLE.SPECIALIST : USER_ROLE.USER;
};

export const setStoredUserRole = (role) => {
  localStorage.setItem(
    USER_ROLE_KEY,
    role === USER_ROLE.SPECIALIST ? USER_ROLE.SPECIALIST : USER_ROLE.USER
  );
};

export const clearStoredUserRole = () => {
  localStorage.removeItem(USER_ROLE_KEY);
};

export const isSpecialistRole = (role) =>
  (role || getStoredUserRole()) === USER_ROLE.SPECIALIST;

export const getCabinetHomePath = (role) =>
  isSpecialistRole(role) ? SPECIALIST_CABINET_PATH : CUSTOMER_CABINET_PATH;

export const resolveMediaUrl = (value) => {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  const absolute =
    /^https?:\/\//i.test(s) || s.startsWith("blob:") || s.startsWith("data:")
      ? s
      : s.startsWith("/")
        ? `${API_ORIGIN}${s}`
        : `${API_ORIGIN}/${s}`;
  return absolute.replace(/ /g, "%20");
};

const unwrap = (payload) => payload?.data ?? payload ?? null;

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  const layer = payload?.data;
  if (Array.isArray(layer)) return layer;
  if (Array.isArray(layer?.data)) return layer.data;
  if (Array.isArray(payload?.certificates)) return payload.certificates;
  if (Array.isArray(layer?.certificates)) return layer.certificates;
  if (Array.isArray(layer?.items)) return layer.items;
  return [];
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const readLocalizedText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") {
    return String(value.az || value.en || value.ru || "").trim();
  }
  return "";
};

const appendLocalizedFields = (target, { title = "", description = "" } = {}) => {
  const write = (key, value) => {
    if (target instanceof FormData) target.append(key, value);
    else target[key] = value;
  };
  ["az", "en", "ru"].forEach((lang) => {
    write(`title[${lang}]`, title);
    write(`description[${lang}]`, description);
  });
};

const pickText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(
      value.name || value.title || value.label || value.full_name || value.text || ""
    ).trim();
  }
  return "";
};

export const normalizeOptionItem = (item, index = 0) => {
  if (item == null) return null;
  if (typeof item === "string" || typeof item === "number") {
    const name = String(item).trim();
    if (!name) return null;
    return { id: name, name };
  }
  if (typeof item !== "object") return null;

  const id = item.id ?? item.service_id ?? item.company_id ?? item.category_id ?? nameFallbackId(item, index);
  const name =
    pickText(item) ||
    pickText(item.service) ||
    pickText(item.company) ||
    pickText(item.category);
  if (id == null && !name) return null;
  return {
    id: id != null ? id : name,
    name: name || String(id),
    slug: item.slug || "",
    raw: item,
  };
};

const nameFallbackId = (item, index) => {
  const name = pickText(item);
  return name ? `tmp-${name}-${index}` : `tmp-${index}`;
};

export const normalizeOptionList = (value) =>
  asArray(value).map(normalizeOptionItem).filter(Boolean);

export const mergeOptionLists = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((item) => {
    if (!item || item.id == null) return;
    const key = String(item.id);
    if (!byId.has(key)) byId.set(key, item);
  });
  return Array.from(byId.values());
};

const normalizeCertificate = (item, index = 0) => {
  if (item == null) return null;
  if (typeof item === "string") {
    const name = item.trim();
    if (!name) return null;
    return { id: `cert-${index}`, name, file: "", preview: "", type: "certificate" };
  }
  if (typeof item !== "object") return null;
  const category =
    (item.certificate_category && typeof item.certificate_category === "object"
      ? item.certificate_category
      : null) || (item.category && typeof item.category === "object" ? item.category : null);
  const pickedFile = pickMaterialFile(item);
  const file = pickedFile.url ? resolveMediaUrl(pickedFile.url) : "";
  const typeRaw = String(
    category?.slug ||
      item.category_slug ||
      item.type ||
      item.kind ||
      item.document_type ||
      (typeof item.category === "string" ? item.category : "")
  ).toLowerCase();
  const type =
    typeRaw.includes("licen") || typeRaw.includes("lisen") ? "license" : typeRaw || "certificate";
  return {
    id: item.id ?? `cert-${index}`,
    name: item.name || pickText(item) || "",
    file,
    preview: file,
    file_name: item.file_name || item.filename || item.original_name || pickedFile.name || "",
    type,
    category_id: item.certificate_category_id ?? item.category_id ?? category?.id ?? null,
    category_name: category?.name || "",
    category_slug: category?.slug || "",
    created_at: item.created_at || "",
    raw: item,
  };
};

const WEEK_TEMPLATE = [
  { week_name: "Monday", week_number: 1 },
  { week_name: "Tuesday", week_number: 2 },
  { week_name: "Wednesday", week_number: 3 },
  { week_name: "Thursday", week_number: 4 },
  { week_name: "Friday", week_number: 5 },
  { week_name: "Saturday", week_number: 6 },
  { week_name: "Sunday", week_number: 7 },
];

export const emptyWorkingHours = () =>
  WEEK_TEMPLATE.map((day) => ({
    week_name: day.week_name,
    week_number: day.week_number,
    working_hours: [],
  }));

const toHourMinute = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return String(value || "").trim();
  return `${String(match[1]).padStart(2, "0")}:${match[2]}`;
};

export const normalizeWorkingHourSlot = (slot) => {
  if (!slot || typeof slot !== "object") return null;
  const start_time = toHourMinute(slot.start_time || slot.start || "");
  const end_time = toHourMinute(slot.end_time || slot.end || "");
  if (!start_time && !end_time) return null;
  const id = slot.id ?? slot.working_hour_id ?? slot.workingHourId ?? null;
  return {
    id,
    start_time,
    end_time,
    weekday: slot.weekday ?? slot.week_number ?? null,
    is_disabled: slot.is_disabled ?? null,
    is_booked: slot.is_booked ?? null,
  };
};

export const normalizeWorkingHours = (value) => {
  const source = Array.isArray(value) ? value : asArray(value);
  const grouped = source.some(
    (item) => Array.isArray(item?.working_hours) || Array.isArray(item?.hours)
  );
  const flatSlots = grouped
    ? []
    : source.map(normalizeWorkingHourSlot).filter(Boolean);

  return WEEK_TEMPLATE.map((template) => {
    if (!grouped) {
      return {
        week_name: template.week_name,
        week_number: template.week_number,
        working_hours: flatSlots.filter(
          (slot) => Number(slot.weekday) === template.week_number
        ),
      };
    }

    const found = source.find(
      (item) =>
        Number(item?.week_number) === template.week_number ||
        Number(item?.weekday) === template.week_number ||
        String(item?.week_name || "").toLowerCase() === template.week_name.toLowerCase()
    );
    const rawSlots = Array.isArray(found?.working_hours)
      ? found.working_hours
      : Array.isArray(found?.hours)
        ? found.hours
        : [];
    const slots = rawSlots.map(normalizeWorkingHourSlot).filter(Boolean);

    return {
      week_name: template.week_name,
      week_number: template.week_number,
      working_hours: slots,
    };
  });
};

export const buildWorkingHoursPayload = (workingHours) =>
  normalizeWorkingHours(workingHours).map((day) => ({
    week_name: day.week_name,
    week_number: day.week_number,
    working_hours: (day.working_hours || []).map((slot) => ({
      start_time: slot.start_time || "",
      end_time: slot.end_time || "",
    })),
  }));

const looksLikeUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const resolveEventType = (item, fallback = "seminar") => {
  const type = String(item?.type || item?.kind || fallback).toLowerCase();
  if (type.includes("webinar")) return "webinar";
  if (type.includes("train")) return "training";
  return "seminar";
};

const normalizeEvent = (item, index = 0, fallbackType = "seminar") => {
  if (item == null) return null;
  if (typeof item === "string") {
    const title = item.trim();
    if (!title) return null;
    return {
      id: `event-${index}`,
      type: fallbackType,
      title,
      description: "",
      date: "",
      location: "",
      meet_link: "",
      mode: fallbackType === "webinar" ? "online" : "offline",
      platform: "",
      status: "active",
    };
  }
  if (typeof item !== "object") return null;
  const type = resolveEventType(item, fallbackType);
  const location = item.location || item.place || item.address || "";
  const meetLink =
    item.meet_link ||
    item.meeting_link ||
    item.meet_url ||
    item.link ||
    (type === "webinar" && looksLikeUrl(location) ? location : "") ||
    "";
  return {
    id: item.id ?? `event-${index}`,
    type,
    title: pickText(item) || "",
    description: item.description || item.note || item.desc || "",
    date: item.date || item.datetime || "",
    location: type === "webinar" && looksLikeUrl(location) ? "" : location,
    meet_link: meetLink,
    mode: item.mode || item.format || (type === "webinar" ? "online" : "offline"),
    platform: item.platform || "",
    status: item.status || "active",
    raw: item,
  };
};

const fileNameFromUrl = (url) => {
  const clean = String(url || "").split("?")[0].split("#")[0];
  const base = clean.split("/").filter(Boolean).pop() || "";
  if (!base || !base.includes(".")) return "";
  try {
    return decodeURIComponent(base);
  } catch {
    return base;
  }
};

const fileFromUnknown = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const url = value.trim();
    return url ? { url, name: fileNameFromUrl(url) } : null;
  }
  if (Array.isArray(value)) return fileFromUnknown(value.find(Boolean));
  if (typeof value !== "object") return null;

  const url = [
    value.url,
    value.file_url,
    value.file_path,
    value.original_url,
    value.full_url,
    value.path,
    value.src,
    typeof value.file === "string" ? value.file : "",
  ].find((entry) => typeof entry === "string" && entry.trim());
  const name =
    value.file_name ||
    value.filename ||
    value.original_name ||
    value.original_filename ||
    (typeof value.name === "string" && value.name.includes(".") ? value.name : "") ||
    "";
  if (url) return { url: String(url).trim(), name: String(name || fileNameFromUrl(url)) };
  if (value.file && typeof value.file === "object") {
    const nested = fileFromUnknown(value.file);
    if (nested) return { url: nested.url, name: name || nested.name };
  }
  return name ? { url: "", name: String(name) } : null;
};

const pickMaterialFile = (item) => {
  const keys = [
    "file",
    "file_url",
    "file_path",
    "asset",
    "asset_file",
    "asset_url",
    "asset_path",
    "media",
    "media_url",
    "document",
    "document_url",
    "attachment",
    "attachment_url",
    "files",
    "attachments",
    "image",
    "certificate_file",
    "document_file",
    "url",
    "path",
  ];
  for (const key of keys) {
    const found = fileFromUnknown(item?.[key]);
    if (found?.url || found?.name) return found;
  }
  return {
    url: "",
    name: item?.file_name || item?.filename || item?.original_name || "",
  };
};

const normalizeMaterial = (item, index = 0) => {
  if (item == null) return null;
  if (typeof item === "string") {
    const title = item.trim();
    if (!title) return null;
    return {
      id: `mat-${index}`,
      title,
      description: "",
      file: "",
      preview: "",
      file_name: "",
      material_type: "document",
    };
  }
  if (typeof item !== "object") return null;
  const category =
    (item.asset_category && typeof item.asset_category === "object" ? item.asset_category : null) ||
    (item.category && typeof item.category === "object" ? item.category : null);
  const pickedFile = pickMaterialFile(item);
  const file = pickedFile.url ? resolveMediaUrl(pickedFile.url) : "";
  const typeValue = item.material_type || (item.type && item.type !== "material" ? item.type : "");
  return {
    id: item.id ?? `mat-${index}`,
    title: readLocalizedText(item.title) || pickText(item) || "",
    description: readLocalizedText(item.description || item.desc || item.note),
    file,
    preview: file,
    file_name: item.file_name || item.filename || item.original_name || pickedFile.name || fileNameFromUrl(file),
    material_type: typeValue || "document",
    category_id: item.asset_category_id ?? item.category_id ?? category?.id ?? null,
    category_name:
      readLocalizedText(category?.translations) ||
      readLocalizedText(category?.name) ||
      category?.name ||
      (typeof item.category === "string" ? item.category : "") ||
      "",
    category_slug: category?.slug || item.category_slug || "",
    status: item.status || item.material_status || "",
    slug: item.slug || "",
    created_at: item.created_at || "",
    updated_at: item.updated_at || "",
    published_at: item.published_at || "",
    downloads_count: item.downloads_count ?? null,
    author_name: item.author?.name || item.author_name || "",
    rejection_reason: item.rejection_reason || "",
    raw: item,
  };
};

export const normalizeSpecialistProfile = (payload) => {
  const raw = unwrap(payload);
  if (!raw || typeof raw !== "object") return null;

  const fullName =
    raw.full_name ||
    raw.name ||
    [raw.first_name, raw.last_name].filter(Boolean).join(" ").trim();
  const nameParts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const taggedEvents = [
    ...asArray(raw.seminars).map((item) => [item, "seminar"]),
    ...asArray(raw.seminar).map((item) => [item, "seminar"]),
    ...asArray(raw.webinars).map((item) => [item, "webinar"]),
    ...asArray(raw.webinar).map((item) => [item, "webinar"]),
    ...asArray(raw.trainings).map((item) => [item, "training"]),
    ...asArray(raw.training).map((item) => [item, "training"]),
  ];

  const seenEvents = new Set();
  const seminars = taggedEvents
    .map(([item, fallbackType], index) => normalizeEvent(item, index, fallbackType))
    .filter(Boolean)
    .filter((item) => {
      const key = `${item.type}|${item.id}|${item.title}|${item.date}`;
      if (seenEvents.has(key)) return false;
      seenEvents.add(key);
      return true;
    });

  return {
    id: raw.id ?? raw.specialist_id ?? null,
    first_name: raw.first_name || nameParts[0] || "",
    last_name: raw.last_name || nameParts.slice(1).join(" ") || "",
    full_name: fullName || "",
    email: raw.email || "",
    phone: raw.phone || raw.phone_number || "",
    address: raw.address || "",
    description: raw.description || raw.about || "",
    profession: pickText(raw.profession) || pickText(raw.speciality) || "",
    profession_id: raw.profession_id ?? raw.profession?.id ?? null,
    image: resolveMediaUrl(raw.image || raw.avatar || raw.photo || ""),
    is_public_working_hours: Boolean(raw.is_public_working_hours),
    services: normalizeOptionList(raw.services || raw.service_ids),
    companies: normalizeOptionList(raw.companies || raw.company || raw.company_ids),
    blog_categories: normalizeOptionList(
      raw.blog_categories || raw.blogCategories || raw.categories || raw.blog_category_ids
    ),
    certificates: asArray(raw.certificates).map(normalizeCertificate).filter(Boolean),
    documents: asArray(
      raw.documents || raw.company_documents || raw.company?.documents
    )
      .map(normalizeCompanyDocument)
      .filter(Boolean),
    working_hours: normalizeWorkingHours(raw.working_hours),
    seminars,
    materials: asArray(raw.materials || raw.material).map(normalizeMaterial).filter(Boolean),
    created_at: raw.created_at || "",
    updated_at: raw.updated_at || "",
    agreement_accepted: raw.agreement_accepted ?? null,
    raw,
  };
};

/** Qaydalar: GET /api/specialist-cabinet/agreement */
export const getSpecialistCabinetAgreement = async () => {
  const response = await request.get("/api/specialist-cabinet/agreement");
  return normalizeAgreement(response.data);
};

/** Qaydaları qəbul et: POST /api/specialist-cabinet/agreement/accept */
export const acceptSpecialistCabinetAgreement = async () => {
  const response = await request.post("/api/specialist-cabinet/agreement/accept", {});
  return response.data;
};

export const getSpecialistPreviewProfile = async () => {
  return getTeamById(SPECIALIST_PREVIEW_ID);
};

export const getSpecialistProfileMe = async () => {
  const response = await request.get("/api/specialist-cabinet/profile/me");
  return response.data;
};

const appendFormValue = (form, key, value) => {
  if (value == null) return;
  if (value instanceof File) {
    form.append(key, value);
    return;
  }
  if (typeof value === "boolean") {
    form.append(key, value ? "1" : "0");
    return;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      form.append(`${key}[]`, "");
      return;
    }
    value.forEach((item, index) => {
      if (item && typeof item === "object" && !(item instanceof File)) {
        Object.entries(item).forEach(([childKey, childValue]) => {
          appendFormValue(form, `${key}[${index}][${childKey}]`, childValue);
        });
        return;
      }
      appendFormValue(form, `${key}[${index}]`, item);
    });
    return;
  }
  form.append(key, value);
};

const unwrapApiStatus = (payload, fallbackMessage) => {
  if (payload?.status === false) {
    const error = new Error(payload.message || fallbackMessage);
    error.response = { data: payload };
    throw error;
  }
  return payload;
};

const isTemporaryOptionId = (id) => {
  const value = String(id ?? "").trim();
  return (
    !value ||
    value.startsWith("tmp-") ||
    value.startsWith("custom-") ||
    value.startsWith("new-")
  );
};

const toRelationObjects = (items) =>
  (items || [])
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "number" && Number.isFinite(item)) return { id: item };
      if (typeof item !== "object" || isTemporaryOptionId(item.id)) return null;
      const id = Number(item.id);
      if (!Number.isFinite(id)) return null;
      const name = String(item.name || item.title || "").trim();
      return name ? { id, name } : { id };
    })
    .filter(Boolean);

export const buildSpecialistUpdatePayload = (profile, files = {}) => {
  const professionId =
    profile.profession_id != null && profile.profession_id !== ""
      ? Number(profile.profession_id)
      : undefined;
  const professionName = String(profile.profession || "").trim();
  const profession = Number.isFinite(professionId)
    ? { id: professionId, ...(professionName ? { name: professionName } : {}) }
    : undefined;

  return {
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    email: profile.email || "",
    phone: profile.phone || "",
    address: profile.address || "",
    description: profile.description || "",
    profession,
    is_public_working_hours: Boolean(profile.is_public_working_hours),
    services: toRelationObjects(profile.services),
    companies: toRelationObjects(profile.companies),
    image: files.image || undefined,
  };
};

const toJsonPayload = (payload) => {
  const jsonPayload = { ...payload };
  delete jsonPayload.image;
  return jsonPayload;
};

const buildSpecialistUpdateForm = (payload) => {
  const form = new FormData();
  form.append("_method", "PUT");
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) return;
    appendFormValue(form, key, value);
  });
  return form;
};

export const updateSpecialistProfile = async (profile, files = {}) => {
  const payload = buildSpecialistUpdatePayload(profile, files);
  const endpoint = "/api/specialist-cabinet/profile/update";

  if (payload.image instanceof File) {
    const response = await request.post(endpoint, buildSpecialistUpdateForm(payload));
    return unwrapApiStatus(response.data, "Profil yenilənmədi");
  }

  const response = await request.put(endpoint, toJsonPayload(payload));
  return unwrapApiStatus(response.data, "Profil yenilənmədi");
};

export const updateSpecialistPassword = async ({
  old_password,
  password,
  password_confirmation,
}) => {
  const response = await request.put("/api/specialist-cabinet/profile/update-password", {
    old_password,
    current_password: old_password,
    password,
    password_confirmation,
  });
  return unwrapApiStatus(response.data, "Şifrə yenilənmədi");
};

/** Sertifikat kateqoriyaları: GET /api/specialist-cabinet/certificates/categories */
export const getSpecialistCertificateCategories = async () => {
  const response = await request.get("/api/specialist-cabinet/certificates/categories");
  const payload = unwrapApiStatus(response.data, "Sertifikat kateqoriyaları yüklənmədi");
  return extractList(payload)
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = item.id ?? item.category_id;
      if (id == null) return null;
      return {
        id,
        name: item.name || item.title || item.slug || String(id),
        slug: item.slug || "",
      };
    })
    .filter(Boolean);
};

/** Sertifikat siyahısı: GET /api/specialist-cabinet/certificates */
export const getSpecialistCertificates = async () => {
  const response = await request.get("/api/specialist-cabinet/certificates");
  const payload = unwrapApiStatus(response.data, "Sertifikatlar yüklənmədi");
  return extractList(payload).map(normalizeCertificate).filter(Boolean);
};

/** Sertifikat yarat: POST /api/specialist-cabinet/certificates/create */
export const createSpecialistCertificate = async ({ name, file, category_id, slug, type }) => {
  const kind = slug || type || "";
  const form = new FormData();
  if (name) form.append("name", name);
  if (file) form.append("file", file);
  if (category_id != null && category_id !== "") {
    form.append("certificate_category_id", String(category_id));
  }
  if (kind) {
    form.append("type", kind);
    form.append("kind", kind);
    form.append("document_type", kind);
  }
  const response = await request.post("/api/specialist-cabinet/certificates/create", form);
  const payload = response.data;
  if (payload?.errors) {
    const error = new Error(payload.message || "Sertifikat yaradılmadı");
    error.response = { data: payload };
    throw error;
  }
  return unwrapApiStatus(payload, "Sertifikat yaradılmadı");
};

/** Sertifikat sil: DELETE /api/specialist-cabinet/certificates/:id */
export const deleteSpecialistCertificate = async (certificateId) => {
  const response = await request.delete(
    `/api/specialist-cabinet/certificates/${encodeURIComponent(certificateId)}`
  );
  return unwrapApiStatus(response.data, "Sertifikat silinmədi");
};

const assertMaterialPayload = (payload, fallbackMessage) => {
  if (payload?.errors) {
    const error = new Error(payload.message || fallbackMessage);
    error.response = { data: payload };
    throw error;
  }
  return unwrapApiStatus(payload, fallbackMessage);
};

const materialListMeta = (payload) => {
  if (payload?.meta && typeof payload.meta === "object") return payload.meta;
  const layer = payload?.data;
  if (layer && typeof layer === "object" && !Array.isArray(layer) && layer.last_page != null) {
    return {
      current_page: layer.current_page,
      last_page: layer.last_page,
      total: layer.total,
    };
  }
  return null;
};

/** Material kateqoriyaları: GET /api/specialist-cabinet/materials/categories */
export const getSpecialistMaterialCategories = async () => {
  const response = await request.get("/api/specialist-cabinet/materials/categories");
  const payload = assertMaterialPayload(response.data, "Material kateqoriyaları yüklənmədi");
  return extractList(payload)
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = item.id ?? item.category_id;
      if (id == null) return null;
      return {
        id,
        name: item.name || item.title || item.slug || String(id),
        slug: item.slug || "",
        type: item.type || "material",
      };
    })
    .filter(Boolean);
};

/**
 * Material siyahısı: GET /api/specialist-cabinet/materials?status=pending
 * @param {{ status?: string, search?: string, category_id?: number|string, page?: number }} [params]
 */
export const getSpecialistMaterials = async (params = {}) => {
  const { status = "", search = "", category_id = "", page = "" } = params;
  const response = await request.get("/api/specialist-cabinet/materials", {
    params: {
      status: status && status !== "all" ? status : undefined,
      search: search || undefined,
      category_id: category_id || undefined,
      page: page || undefined,
    },
  });
  const payload = assertMaterialPayload(response.data, "Materiallar yüklənmədi");
  return {
    data: extractList(payload).map(normalizeMaterial).filter(Boolean),
    meta: materialListMeta(payload),
  };
};

/** Material detalları: GET /api/specialist-cabinet/materials/:material_id */
export const getSpecialistMaterialById = async (materialId) => {
  const response = await request.get(
    `/api/specialist-cabinet/materials/${encodeURIComponent(materialId)}`
  );
  const payload = assertMaterialPayload(response.data, "Material yüklənmədi");
  const raw = unwrap(payload);
  const item = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  return normalizeMaterial(item);
};

/** Material yarat: POST /api/specialist-cabinet/materials/create */
export const createSpecialistMaterial = async ({ title, description, category_id, file }) => {
  const form = new FormData();
  appendLocalizedFields(form, { title: title || "", description: description || "" });
  if (category_id != null && category_id !== "") {
    form.append("asset_category_id", String(category_id));
  }
  if (file instanceof File) form.append("file", file);
  const response = await request.post("/api/specialist-cabinet/materials/create", form);
  return assertMaterialPayload(response.data, "Material yaradılmadı");
};

/** Material yenilə: POST /api/specialist-cabinet/materials/:material_id */
export const updateSpecialistMaterial = async (materialId, data = {}) => {
  const form = new FormData();
  appendLocalizedFields(form, {
    title: data.title || "",
    description: data.description || "",
  });
  if (data.category_id != null && data.category_id !== "") {
    form.append("asset_category_id", String(data.category_id));
  }
  if (data.file instanceof File) form.append("file", data.file);
  const response = await request.post(
    `/api/specialist-cabinet/materials/${encodeURIComponent(materialId)}`,
    form
  );
  return assertMaterialPayload(response.data, "Material yenilənmədi");
};

/** Material sil: DELETE /api/specialist-cabinet/materials/:material_id */
export const deleteSpecialistMaterial = async (materialId) => {
  const response = await request.delete(
    `/api/specialist-cabinet/materials/${encodeURIComponent(materialId)}`
  );
  return assertMaterialPayload(response.data, "Material silinmədi");
};

const BLOG_LOCALES = ["az", "en", "ru"];

const asLocaleMap = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      az: String(value.az || ""),
      en: String(value.en || ""),
      ru: String(value.ru || ""),
    };
  }
  const text = value == null ? "" : String(value);
  return { az: text, en: "", ru: "" };
};

const filledLocaleMap = (value) => {
  const map = asLocaleMap(value);
  const az = map.az || map.en || map.ru;
  return { az, en: map.en || az, ru: map.ru || az };
};

const appendLocaleMap = (form, key, value) => {
  const map = filledLocaleMap(value);
  BLOG_LOCALES.forEach((lang) => form.append(`${key}[${lang}]`, map[lang]));
};

const normalizeBlogCategory = (item) => {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? item.category_id;
  if (id == null) return null;
  const translations = asLocaleMap(item.translations || item.name);
  return {
    id,
    name: readLocalizedText(item.translations) || readLocalizedText(item.name) || item.name || "",
    translations,
    slug: item.slug || "",
    is_published: Boolean(item.is_published),
    is_own: Boolean(item.is_own),
  };
};

const normalizeBlog = (item) => {
  if (!item || typeof item !== "object") return null;
  const category = item.category && typeof item.category === "object" ? item.category : null;
  const cover = fileFromUnknown(item.cover_image) || fileFromUnknown(item.cover) || fileFromUnknown(item.image);
  const coverUrl = cover?.url ? resolveMediaUrl(cover.url) : "";
  return {
    id: item.id,
    title: readLocalizedText(item.title) || "",
    titles: asLocaleMap(item.title),
    short_content: readLocalizedText(item.short_content) || "",
    shorts: asLocaleMap(item.short_content),
    full_content: readLocalizedText(item.full_content) || "",
    fulls: asLocaleMap(item.full_content),
    slug: item.slug || "",
    cover: coverUrl,
    category_id: item.blog_category_id ?? item.category_id ?? category?.id ?? null,
    category_name:
      (typeof category?.name === "string" ? category.name : "") ||
      readLocalizedText(category?.name) ||
      readLocalizedText(category?.translations) ||
      "",
    category_slug: category?.slug || "",
    status: item.status || "",
    rejection_reason: item.rejection_reason || "",
    is_published: Boolean(item.is_published),
    views_count: item.views_count ?? null,
    published_at: item.published_at || "",
    created_at: item.created_at || "",
    updated_at: item.updated_at || "",
  };
};

const buildBlogForm = (data = {}) => {
  const form = new FormData();
  appendLocaleMap(form, "title", data.title);
  appendLocaleMap(form, "short_content", data.short_content);
  appendLocaleMap(form, "full_content", data.full_content);
  if (data.category_id != null && data.category_id !== "") {
    form.append("blog_category_id", String(data.category_id));
    form.append("category_id", String(data.category_id));
  }
  if (data.cover instanceof File) form.append("cover_image", data.cover);
  return form;
};

/** Bloq kateqoriyaları: GET /api/specialist-cabinet/blogs/categories */
export const getSpecialistCabinetBlogCategories = async () => {
  const response = await request.get("/api/specialist-cabinet/blogs/categories");
  const payload = assertMaterialPayload(response.data, "Bloq kateqoriyaları yüklənmədi");
  return extractList(payload).map(normalizeBlogCategory).filter(Boolean);
};

/** Bloq kateqoriyası yarat: POST /api/specialist-cabinet/blogs/categories */
export const createSpecialistBlogCategory = async (names) => {
  const translations = filledLocaleMap(names);
  const response = await request.post("/api/specialist-cabinet/blogs/categories", {
    name: translations,
    translations,
  });
  return assertMaterialPayload(response.data, "Kateqoriya yaradılmadı");
};

/** Bloq siyahısı: GET /api/specialist-cabinet/blogs */
export const getSpecialistBlogs = async (params = {}) => {
  const { status = "", page = "" } = params;
  const response = await request.get("/api/specialist-cabinet/blogs", {
    params: {
      status: status && status !== "all" ? status : undefined,
      page: page || undefined,
    },
  });
  const payload = assertMaterialPayload(response.data, "Bloqlar yüklənmədi");
  return {
    data: extractList(payload).map(normalizeBlog).filter(Boolean),
    meta: materialListMeta(payload),
  };
};

/** Bloq detalları: GET /api/specialist-cabinet/blogs/:id */
export const getSpecialistBlogById = async (blogId) => {
  const response = await request.get(`/api/specialist-cabinet/blogs/${encodeURIComponent(blogId)}`);
  const payload = assertMaterialPayload(response.data, "Bloq yüklənmədi");
  const raw = unwrap(payload);
  const item = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  return normalizeBlog(item);
};

/** Bloq yarat: POST /api/specialist-cabinet/blogs/create */
export const createSpecialistBlog = async (data) => {
  const response = await request.post("/api/specialist-cabinet/blogs/create", buildBlogForm(data));
  return assertMaterialPayload(response.data, "Bloq yaradılmadı");
};

/** Bloq yenilə: POST /api/specialist-cabinet/blogs/:id */
export const updateSpecialistBlog = async (blogId, data) => {
  const response = await request.post(
    `/api/specialist-cabinet/blogs/${encodeURIComponent(blogId)}`,
    buildBlogForm(data)
  );
  return assertMaterialPayload(response.data, "Bloq yenilənmədi");
};

/** Bloq sil: DELETE /api/specialist-cabinet/blogs/:id */
export const deleteSpecialistBlog = async (blogId) => {
  const response = await request.delete(`/api/specialist-cabinet/blogs/${encodeURIComponent(blogId)}`);
  return assertMaterialPayload(response.data, "Bloq silinmədi");
};

const toApiDateTime = (value) => {
  const raw = String(value || "").trim().replace("T", " ");
  if (!raw) return "";
  return raw.slice(0, 16);
};

const normalizeSeminar = (item) => {
  if (!item || typeof item !== "object") return null;
  return {
    id: item.id,
    type: item.type || "seminar",
    title: readLocalizedText(item.title) || "",
    titles: asLocaleMap(item.title),
    description: readLocalizedText(item.description) || "",
    descriptions: asLocaleMap(item.description),
    location: readLocalizedText(item.location) || "",
    locations: asLocaleMap(item.location),
    category: readLocalizedText(item.category) || "",
    categories: asLocaleMap(item.category),
    level: readLocalizedText(item.level) || "",
    levels: asLocaleMap(item.level),
    date: item.date || "",
    mode: item.mode || "",
    platform: item.platform || "",
    link: item.link || item.meet_link || "",
    status: item.status || "",
    approval_status: item.approval_status || "",
    rejection_reason: item.rejection_reason || "",
    participants_count: item.participants_count,
    created_at: item.created_at || "",
    updated_at: item.updated_at || "",
  };
};

const buildSeminarForm = (data = {}) => {
  const form = new FormData();
  form.append("type", data.type || "seminar");
  appendLocaleMap(form, "title", data.title);
  appendLocaleMap(form, "description", data.description);
  appendLocaleMap(form, "location", data.location);
  appendLocaleMap(form, "category", data.category);
  appendLocaleMap(form, "level", data.level);
  const date = toApiDateTime(data.date);
  if (date) form.append("date", date);
  if (data.mode) form.append("mode", data.mode);
  if (data.platform) form.append("platform", data.platform);
  if (data.link) form.append("link", data.link);
  return form;
};

/** Seminar siyahısı: GET /api/specialist-cabinet/seminars */
export const getSpecialistSeminars = async (params = {}) => {
  const { type = "", status = "", page = "" } = params;
  const response = await request.get("/api/specialist-cabinet/seminars", {
    params: {
      type: type || undefined,
      status: status && status !== "all" ? status : undefined,
      page: page || undefined,
    },
  });
  const payload = assertMaterialPayload(response.data, "Tədbirlər yüklənmədi");
  return {
    data: extractList(payload).map(normalizeSeminar).filter(Boolean),
    meta: materialListMeta(payload),
  };
};

/** Seminar detalları: GET /api/specialist-cabinet/seminars/:id */
export const getSpecialistSeminarById = async (seminarId) => {
  const response = await request.get(
    `/api/specialist-cabinet/seminars/${encodeURIComponent(seminarId)}`
  );
  const payload = assertMaterialPayload(response.data, "Tədbir yüklənmədi");
  const raw = unwrap(payload);
  const item = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  return normalizeSeminar(item);
};

/** Seminar yarat: POST /api/specialist-cabinet/seminars/create */
export const createSpecialistSeminar = async (data) => {
  const response = await request.post("/api/specialist-cabinet/seminars/create", buildSeminarForm(data));
  return assertMaterialPayload(response.data, "Tədbir yaradılmadı");
};

/** Seminar yenilə: PUT /api/specialist-cabinet/seminars/:id */
export const updateSpecialistSeminar = async (seminarId, data = {}) => {
  const date = toApiDateTime(data.date);
  const response = await request.put(
    `/api/specialist-cabinet/seminars/${encodeURIComponent(seminarId)}`,
    {
      type: data.type || "seminar",
      title: filledLocaleMap(data.title),
      description: filledLocaleMap(data.description),
      location: filledLocaleMap(data.location),
      category: filledLocaleMap(data.category),
      level: filledLocaleMap(data.level),
      date: date || undefined,
      mode: data.mode || undefined,
      platform: data.platform || undefined,
      link: data.link || undefined,
    }
  );
  return assertMaterialPayload(response.data, "Tədbir yenilənmədi");
};

/** Seminar sil: DELETE /api/specialist-cabinet/seminars/:id */
export const deleteSpecialistSeminar = async (seminarId) => {
  const response = await request.delete(
    `/api/specialist-cabinet/seminars/${encodeURIComponent(seminarId)}`
  );
  return assertMaterialPayload(response.data, "Tədbir silinmədi");
};

export const getSpecialistWorkingHours = async () => {
  const response = await request.get("/api/specialist-cabinet/working-hours");
  const payload = unwrapApiStatus(response.data, "İş saatları yüklənmədi");
  const days = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : extractList(payload);
  return normalizeWorkingHours(days);
};

export const createSpecialistWorkingHour = async ({
  weekday,
  start_time,
  end_time,
}) => {
  const response = await request.post("/api/specialist-cabinet/working-hours/create", {
    weekday: Number(weekday),
    start_time: toHourMinute(start_time) || "09:00",
    end_time: toHourMinute(end_time) || "18:00",
  });
  const payload = response.data;
  if (payload?.errors) {
    const error = new Error(payload.message || "İş saatı yaradılmadı");
    error.response = { data: payload };
    throw error;
  }
  return unwrapApiStatus(payload, "İş saatı yaradılmadı");
};

export const updateSpecialistWorkingHour = async (workingHourId, data = {}) => {
  const response = await request.put(`/api/specialist-cabinet/working-hours/${workingHourId}`, {
    start_time: toHourMinute(data.start_time) || "",
    end_time: toHourMinute(data.end_time) || "",
    is_disabled: Boolean(data.is_disabled),
  });
  const payload = response.data;
  if (payload?.errors) {
    const error = new Error(payload.message || "İş saatı yenilənmədi");
    error.response = { data: payload };
    throw error;
  }
  return unwrapApiStatus(payload, "İş saatı yenilənmədi");
};

export const deleteSpecialistWorkingHour = async (workingHourId) => {
  const response = await request.delete(`/api/specialist-cabinet/working-hours/${workingHourId}`);
  if (response?.data?.errors) {
    const error = new Error(response.data.message || "İş saatı silinmədi");
    error.response = { data: response.data };
    throw error;
  }
  return unwrapApiStatus(response.data ?? { status: true }, "İş saatı silinmədi");
};

export const updateSpecialistWorkingHours = async (workingHours) => {
  const days = buildWorkingHoursPayload(workingHours);
  const response = await request.put("/api/specialist-cabinet/working-hours", {
    working_hours: days,
  });
  const payload = unwrapApiStatus(response.data, "İş saatları yenilənmədi");
  const list = extractList(payload);
  return {
    ...(payload || {}),
    data: list.length ? normalizeWorkingHours(list) : normalizeWorkingHours(days),
  };
};

export const unwrapAppointmentRecord = (payload, appointmentId) => {
  let layer = payload?.data !== undefined ? payload.data : payload;
  if (layer?.appointment && typeof layer.appointment === "object" && layer.appointment.id != null) {
    layer = layer.appointment;
  }
  if (Array.isArray(layer)) {
    const wanted =
      appointmentId != null
        ? layer.find((item) => {
            const rec = item?.appointment ?? item;
            return String(rec?.id) === String(appointmentId);
          })
        : layer[0];
    if (!wanted) return null;
    return wanted.appointment && typeof wanted.appointment === "object"
      ? wanted.appointment
      : wanted;
  }
  if (layer && typeof layer === "object") return layer;
  return null;
};

export const getSpecialistAppointments = async (params = {}) => {
  const query = { page: params.page || 1 };
  if (params.per_page) query.per_page = params.per_page;
  const response = await request.get("/api/specialist-cabinet/appointments", {
    params: query,
  });
  return response.data;
};

export const getSpecialistAppointmentStatistics = async () => {
  const response = await request.get("/api/specialist-cabinet/appointments/get-statistics");
  const payload = unwrapApiStatus(response.data, "Statistika yüklənmədi");
  const layer = payload?.data !== undefined ? payload.data : payload;
  if (Array.isArray(layer)) return layer;
  if (Array.isArray(layer?.data)) return layer.data;
  return [];
};

export const getSpecialistAppointmentsCalendar = async (params = {}) => {
  const response = await request.get("/api/specialist-cabinet/appointments/calendar", {
    params,
  });
  return response.data;
};

export const getSpecialistAppointmentById = async (appointmentId) => {
  const response = await request.get(`/api/specialist-cabinet/appointments/${appointmentId}`);
  const data = unwrapAppointmentRecord(response.data, appointmentId);
  return { ...(response.data || {}), data };
};

export const updateSpecialistAppointment = async (appointmentId, data = {}) => {
  const response = await request.put(
    `/api/specialist-cabinet/appointments/${appointmentId}`,
    data
  );
  const payload = unwrapApiStatus(response.data, "Randevu yenilənmədi");
  const record = unwrapAppointmentRecord(payload, appointmentId);
  return { ...(payload || {}), data: record || payload?.data };
};

const unwrapList = (payload) => {
  const raw = unwrap(payload);
  return Array.isArray(raw) ? raw : asArray(raw);
};

export const getSpecialistServiceOptions = async () => {
  const payload = await getAllServices();
  return normalizeOptionList(unwrapList(payload));
};

export const getSpecialistProfessionOptions = async () => {
  const payload = await getTeamProfessions();
  return normalizeOptionList(unwrapList(payload));
};

export const getSpecialistBlogCategoryOptions = async () => {
  const payload = await getBlogCategories();
  return normalizeOptionList(unwrapList(payload));
};

export const SPECIALIST_AGREEMENT_ID = "specialist-agreement";
const SPECIALIST_DOCS_ACK_KEY = "lifechart.specialistDocumentsAck";

export function getSpecialistFileDocuments(profile) {
  const layer = profile?.raw && typeof profile.raw === "object" ? profile.raw : profile || {};
  const raw =
    profile?.documents ||
    layer?.documents ||
    layer?.company_documents ||
    layer?.company?.documents ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeCompanyDocument).filter(Boolean);
}

function getSpecialistAckUserId(profile) {
  return String(profile?.id ?? profile?.user_id ?? profile?.email ?? "");
}

export function getSpecialistDocumentsFingerprint(profile) {
  return [
    SPECIALIST_AGREEMENT_ID,
    ...getSpecialistFileDocuments(profile).map((doc) => String(doc.id)),
  ].join("|");
}

export function hasAcknowledgedSpecialistDocuments(profile) {
  try {
    const raw = sessionStorage.getItem(SPECIALIST_DOCS_ACK_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return (
      parsed?.userId === getSpecialistAckUserId(profile) &&
      parsed?.fingerprint === getSpecialistDocumentsFingerprint(profile)
    );
  } catch {
    return false;
  }
}

export function acknowledgeSpecialistDocuments(profile) {
  sessionStorage.setItem(
    SPECIALIST_DOCS_ACK_KEY,
    JSON.stringify({
      userId: getSpecialistAckUserId(profile),
      fingerprint: getSpecialistDocumentsFingerprint(profile),
    })
  );
}

export function clearSpecialistDocumentsAck() {
  try {
    sessionStorage.removeItem(SPECIALIST_DOCS_ACK_KEY);
  } catch {
    // ignore storage errors
  }
}
